import type { MentionedChannel, MentionedRole, MentionedUser } from '~/types/messages'
import { ChannelType } from '~/types/groups'
import { useGroupsStore } from '~/stores/groups'
import type { ChannelLinkTarget } from '~/lib/chat/channelSuggestions'
import type { UserProfileDialogUser } from '~/composables/useUserProfileDialog'
import { normalizeMentionText } from '~/lib/chat/mentionSuggestions'

// Discord-style mention tokens in message content: `<@{id}>` names a user/bot/agent, `<#{id}>` a
// channel (ids are GUIDs, matched case-insensitively). They are what's stored and sent; people never
// see them. The composer keeps readable `@username` / `#channel name` (its highlight overlay has to
// stay character-identical to the textarea), `encodeMentions` turns that into tokens on send/edit
// save, `decodeMentions` turns tokens back into readable text for editing and plain-text contexts
// (reply snippets, copy, search). Rendered messages show them as pills (mentionHighlight.ts).
//
// Old messages written before tokens existed keep their plain `@username` / `#name`, which the
// renderer still highlights the old way. `@all` is never encoded — it stays plain text.
//
// `<@&{id}>` names a role (readable `@Role name` in the composer). `@everyone` / `@here` are tokens
// as they are — plain text that the server recognizes (when the author may mention everyone).

const GUID = '[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}'

/** `<@id>`, `<@&id>` or `<#id>`: group 1 is the sigil (`@` / `@&` / `#`), group 2 the id. Global — use with replace/split/matchAll. */
export const MENTION_TOKEN_PATTERN = new RegExp(`<(@&|[@#])(${GUID})>`, 'g')
/** The same as a capturing split pattern: `text.split(MENTION_TOKEN_SPLIT)` alternates text, sigil, id. */
export const MENTION_TOKEN_SPLIT = new RegExp(`<(@&|[@#])(${GUID})>`)

/** Broadcast mentions — not members, never encoded, rendered as pills. */
export const BROADCAST_MENTIONS = ['everyone', 'here'] as const

/** Shown for a `<@id>` nobody can resolve (left the group, deleted account, not loaded anywhere). */
export const UNKNOWN_USER_LABEL = 'неизвестный'
/** Shown for a `<#id>` nobody can resolve (deleted, or a channel this client can't see). */
export const DELETED_CHANNEL_LABEL = 'удалённый-канал'
/** Shown for a `<@&id>` nobody can resolve (a deleted role). */
export const DELETED_ROLE_LABEL = 'удалённая-роль'

export function hasMentionTokens(text: string | null | undefined): boolean {
  return !!text && text.includes('<') && MENTION_TOKEN_SPLIT.test(text)
}

// ── Who / what a token names ──────────────────────────────────────────────────

/** Anything user-shaped a mention can be resolved from (GroupUser, User, MentionedUser). */
export interface MentionUserLike {
  id: string
  username: string
  display_name?: string | null
  avatar_url?: string | null
  is_bot?: boolean | null
}

export interface MentionSource {
  mentions?: MentionedUser[] | null
  mention_channels?: MentionedChannel[] | null
  mention_roles?: MentionedRole[] | null
}

export interface MentionLookups {
  user(id: string): MentionedUser | null
  channel(id: string): MentionedChannel | null
  role(id: string): MentionedRole | null
}

/** `#rrggbb` (a group role's color) → the RGB integer `MentionedRole.color` carries; 0 when unset. */
export function roleColorToInt(color: string | null | undefined): number {
  const hex = color?.match(/^#([0-9a-f]{6})$/i)?.[1]
  return hex ? parseInt(hex, 16) : 0
}

/** `MentionedRole.color` → `#rrggbb`, or '' for no color. */
export function roleColorToHex(color: number | null | undefined): string {
  return color ? `#${(color & 0xffffff).toString(16).padStart(6, '0')}` : ''
}

function toMentionedUser(user: MentionUserLike): MentionedUser {
  return {
    id: user.id,
    username: user.username,
    display_name: user.display_name ?? '',
    avatar_url: user.avatar_url ?? '',
    is_bot: !!user.is_bot,
  }
}

// Last-resort directory of users/channels seen on this client outside the store — the composer's
// server-side member search results and whatever it picked, server-resolved mentions of messages.
// Lets a freshly sent message (and its optimistic copy) render the pill for someone who was only
// ever found through search. Not reactive on purpose: it's a fallback, the store is the live source.
const knownUsers = new Map<string, MentionedUser>()
const knownChannels = new Map<string, MentionedChannel>()

export function rememberMentionUsers(users: Iterable<MentionUserLike>) {
  for (const user of users) {
    if (user.id && user.username) knownUsers.set(user.id.toLowerCase(), toMentionedUser(user))
  }
}

export function rememberMentionChannels(channels: Iterable<MentionedChannel>) {
  for (const channel of channels) {
    if (channel.id && channel.name) knownChannels.set(channel.id.toLowerCase(), channel)
  }
}

/**
 * Resolves tokens for one message: what the server attached to it (`mentions` / `mention_channels`)
 * first, then the store — the message's own group, then every loaded group — then the directory
 * above. Lookups read the store lazily, so calling them inside a computed tracks it.
 */
export function buildMentionLookups(source?: MentionSource | null, groupId?: string | null): MentionLookups {
  const groupsStore = useGroupsStore()

  function user(id: string): MentionedUser | null {
    const key = id.toLowerCase()
    const own = source?.mentions?.find((u) => u.id.toLowerCase() === key)
    if (own) return own
    const lists = groupId ? [groupsStore.groupUsers[groupId], ...Object.values(groupsStore.groupUsers)] : Object.values(groupsStore.groupUsers)
    for (const list of lists) {
      const member = list?.find((u) => u.id.toLowerCase() === key)
      if (member?.username) return toMentionedUser(member)
    }
    return knownUsers.get(key) ?? null
  }

  function channelOfGroup(gid: string, key: string): MentionedChannel | null {
    for (const category of groupsStore.groupDetails[gid]?.categories ?? []) {
      const found = category.channels.find((c) => c.id.toLowerCase() === key)
      if (found) return { id: found.id, name: found.name, type: found.type, group_id: gid }
    }
    return null
  }

  function channel(id: string): MentionedChannel | null {
    const key = id.toLowerCase()
    const own = source?.mention_channels?.find((c) => c.id.toLowerCase() === key)
    if (own) return own
    if (groupId) {
      const found = channelOfGroup(groupId, key)
      if (found) return found
    }
    for (const gid of Object.keys(groupsStore.groupDetails)) {
      if (gid === groupId) continue
      const found = channelOfGroup(gid, key)
      if (found) return found
    }
    return knownChannels.get(key) ?? null
  }

  function role(id: string): MentionedRole | null {
    const key = id.toLowerCase()
    const own = source?.mention_roles?.find((r) => r.id.toLowerCase() === key)
    if (own) return own
    const lists = groupId ? [groupsStore.groupRoles[groupId], ...Object.values(groupsStore.groupRoles)] : Object.values(groupsStore.groupRoles)
    for (const list of lists) {
      const found = list?.find((r) => r.id.toLowerCase() === key)
      if (found) return { id: found.id, name: found.name, color: roleColorToInt(found.color) }
    }
    return null
  }

  return { user, channel, role }
}

/**
 * Who a clicked `<@id>` pill opens: the loaded member (its group first) when there is one, so the
 * profile has roles and status; otherwise a minimal profile from what the message's mentions say.
 */
export function resolveMentionProfileUser(
  userId: string,
  lookups: MentionLookups,
  groupId?: string | null,
): UserProfileDialogUser | null {
  const groupsStore = useGroupsStore()
  const key = userId.toLowerCase()
  const lists = groupId ? [groupsStore.groupUsers[groupId], ...Object.values(groupsStore.groupUsers)] : Object.values(groupsStore.groupUsers)
  for (const list of lists) {
    const member = list?.find((u) => u.id.toLowerCase() === key)
    if (member) return member
  }
  const user = lookups.user(userId)
  return user ? { ...user } : null
}

/** Every user/channel the content's tokens name, as far as this client can resolve them. */
export function resolveContentMentions(
  content: string,
  lookups: MentionLookups,
): { mentions: MentionedUser[], mention_channels: MentionedChannel[] } {
  const mentions: MentionedUser[] = []
  const mentionChannels: MentionedChannel[] = []
  const seen = new Set<string>()
  for (const [, sigil, id] of content.matchAll(MENTION_TOKEN_PATTERN)) {
    const key = `${sigil}${id!.toLowerCase()}`
    if (seen.has(key)) continue
    seen.add(key)
    // Roles are the server's to confirm (they only count when the author may mention them).
    if (sigil === '@&') continue
    if (sigil === '@') {
      const user = lookups.user(id!)
      if (user) mentions.push(user)
    } else {
      const channel = lookups.channel(id!)
      if (channel) mentionChannels.push(channel)
    }
  }
  return { mentions, mention_channels: mentionChannels }
}

// ── Tokens → readable ─────────────────────────────────────────────────────────

/** `<@id>` → `@username`, `<#id>` → `#name`; unknown ones get a placeholder name. */
export function decodeMentions(content: string, lookups?: MentionLookups | null): string {
  if (!content || !content.includes('<')) return content
  const resolve = lookups ?? buildMentionLookups()
  return content.replace(MENTION_TOKEN_PATTERN, (_, sigil: string, id: string) => (
    sigil === '@'
      ? `@${resolve.user(id)?.username || UNKNOWN_USER_LABEL}`
      : sigil === '@&'
        ? `@${resolve.role(id)?.name || DELETED_ROLE_LABEL}`
        : `#${resolve.channel(id)?.name || DELETED_CHANNEL_LABEL}`
  ))
}

/** Readable text of a message (its own `mentions` first) — for copy, snippets and the edit flow. */
export function decodeMessageContent(
  message: (MentionSource & { content?: string | null }) | null | undefined,
  groupId?: string | null,
): string {
  const content = message?.content ?? ''
  if (!hasMentionTokens(content)) return content
  return decodeMentions(content, buildMentionLookups(message, groupId))
}

// A search highlight fragment is a cut-out of the raw content, so it can start or end in the middle
// of a token. Those halves can't be resolved — drop them rather than show GUID debris. The tail of a
// cut token only counts as one when it's clearly GUID-shaped (has a dash, or is the full last group),
// so ordinary text ending in `>` survives. Run it after `decodeMentions`, which takes the whole
// tokens out first — the start pattern would otherwise also eat a complete leading token.
const TOKEN_TAIL_AT_START = /^<?(?:@&|[@#&])?(?:[0-9a-fA-F]*-[0-9a-fA-F-]*|[0-9a-fA-F]{12})>/
const TOKEN_HEAD_AT_END = /<(?:(?:@&?|#)[0-9a-fA-F-]*)?$/

export function stripPartialMentionTokens(fragment: string): string {
  return fragment.replace(TOKEN_TAIL_AT_START, '').replace(TOKEN_HEAD_AT_END, '')
}

// ── Readable → tokens ─────────────────────────────────────────────────────────

export interface MentionEncodeContext {
  /** Lowercased username → user id, or null when it isn't a user we can name. */
  resolveUser(usernameLower: string): string | null
  /**
   * Channels a `#name` may name, longest name first (so `#general chat` wins over `#general`);
   * for two channels with the same name the earlier one wins.
   */
  channels: ChannelLinkTarget[]
  /** Roles an `@Role name` may name, longest name first — only those the author may mention. */
  roles?: ChannelLinkTarget[]
}

// Same shape as the renderer's legacy `@username` (mentionHighlight.ts): "@" at the start or after
// whitespace. Letters of any script here, since a username that doesn't match simply stays text.
// Markdown-escaped characters (`@user\_name`, as the Markdown editor serializes it) count too.
const USERNAME_MENTION_PATTERN = /(^|\s)@((?:[\p{L}\p{N}_.-]|\\[_.-])+)/gu
const MARKDOWN_ESCAPE = /\\(.)/g
const TRAILING_PUNCTUATION = /[.-]+$/
// A name only counts when it ends here — not halfway through a longer word (`#general` in `#generals`).
const CHANNEL_NAME_CONTINUES = /[\p{L}\p{N}_-]/u

// Code spans and fenced blocks are left alone: a token there would show up raw, never as a pill.
const CODE_PATTERN = /```[\s\S]*?(?:```|$)|(`+)[\s\S]*?[^`]\1(?!`)/g

function mapOutsideCode(text: string, map: (part: string) => string): string {
  let result = ''
  let lastIndex = 0
  for (const match of text.matchAll(CODE_PATTERN)) {
    result += map(text.slice(lastIndex, match.index)) + match[0]
    lastIndex = match.index! + match[0].length
  }
  return result + map(text.slice(lastIndex))
}

function encodeChannels(text: string, channels: ChannelLinkTarget[]): string {
  if (channels.length === 0 || !text.includes('#')) return text
  let result = ''
  let lastIndex = 0
  const startsWord = (at: number) => at === 0 || /\s/.test(text[at - 1]!)
  for (let i = text.indexOf('#'); i !== -1; i = text.indexOf('#', i + 1)) {
    if (i < lastIndex) continue
    // `\#name` is how the Markdown editor serializes a "#" it would otherwise read as a heading.
    const escaped = i > 0 && text[i - 1] === '\\' && startsWord(i - 1)
    if (!escaped && !startsWord(i)) continue
    const target = channels.find((t) =>
      normalizeMentionText(text.slice(i + 1, i + 1 + t.name.length)) === t.key
      && !CHANNEL_NAME_CONTINUES.test(text[i + 1 + t.name.length] ?? ''))
    if (!target) continue
    result += `${text.slice(lastIndex, escaped ? i - 1 : i)}<#${target.id}>`
    lastIndex = i + 1 + target.name.length
  }
  return result + text.slice(lastIndex)
}

function encodeUsers(text: string, resolveUser: MentionEncodeContext['resolveUser']): string {
  if (!text.includes('@')) return text
  return text.replace(USERNAME_MENTION_PATTERN, (whole, lead: string, raw: string) => {
    const name = raw.replace(MARKDOWN_ESCAPE, '$1')
    // `@all` / `@everyone` / `@here` are broadcasts, not members — they stay plain text.
    const lower = name.toLowerCase()
    if (lower === 'all' || (BROADCAST_MENTIONS as readonly string[]).includes(lower)) return whole
    // "@john." — a sentence's punctuation right after a name isn't part of it.
    let candidate = name
    while (candidate) {
      const id = resolveUser(candidate.toLowerCase())
      if (id) return `${lead}<@${id}>${name.slice(candidate.length)}`
      const trimmed = candidate.replace(TRAILING_PUNCTUATION, '')
      if (trimmed === candidate) break
      candidate = trimmed
    }
    return whole
  })
}

// `@Role name` → `<@&id>`: like a channel name, a role name may contain spaces, so each "@" is
// tried against the names longest first. The token's "@" is followed by "&", so the user pass
// below leaves it alone.
function encodeRoles(text: string, roles: ChannelLinkTarget[] | undefined): string {
  if (!roles?.length || !text.includes('@')) return text
  let result = ''
  let lastIndex = 0
  for (let i = text.indexOf('@'); i !== -1; i = text.indexOf('@', i + 1)) {
    if (i < lastIndex || (i > 0 && !/\s/.test(text[i - 1]!))) continue
    const target = roles.find((t) =>
      normalizeMentionText(text.slice(i + 1, i + 1 + t.name.length)) === t.key
      && !CHANNEL_NAME_CONTINUES.test(text[i + 1 + t.name.length] ?? ''))
    if (!target) continue
    result += `${text.slice(lastIndex, i)}<@&${target.id}>`
    lastIndex = i + 1 + target.name.length
  }
  return result + text.slice(lastIndex)
}

/** Readable composer text → content with tokens. Unresolvable names stay as typed. */
export function encodeMentions(text: string, context: MentionEncodeContext): string {
  if (!text.includes('@') && !text.includes('#')) return text
  // Channels first: a token they produce has no "@" for the user pass to trip over; roles before
  // users so a picked role wins over a member whose username is its first word.
  return mapOutsideCode(text, (part) => encodeUsers(encodeRoles(encodeChannels(part, context.channels), context.roles), context.resolveUser))
}

/** A group's channels as encode targets (DMs left out, longest name first). */
export function channelEncodeTargets(groupId: string | null | undefined): ChannelLinkTarget[] {
  if (!groupId) return []
  const groupsStore = useGroupsStore()
  const targets: ChannelLinkTarget[] = []
  for (const category of groupsStore.groupDetails[groupId]?.categories ?? []) {
    for (const channel of category.channels) {
      if (channel.type === ChannelType.DirectMessage || channel.type === ChannelType.GroupMessage || !channel.name) continue
      targets.push({ id: channel.id, name: channel.name, key: normalizeMentionText(channel.name) })
    }
  }
  return targets
}

// ── Does a message mention me ─────────────────────────────────────────────────

const LEGACY_MENTION_PATTERN = /(^|\s)@([A-Za-z0-9_.-]+)/g

/** `<@myId>`, a legacy plain `@myusername`, or `@all`. */
export function contentMentionsUser(content: string | null | undefined, userId: string | null | undefined, username?: string | null): boolean {
  if (!content || !content.includes('@')) return false
  if (userId && content.toLowerCase().includes(`<@${userId.toLowerCase()}>`)) return true
  const name = username?.toLowerCase()
  for (const [, , handle] of content.matchAll(LEGACY_MENTION_PATTERN)) {
    const h = handle!.toLowerCase().replace(TRAILING_PUNCTUATION, '')
    if (h === 'all' || (name && h === name)) return true
  }
  return false
}
